import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  Home,
  Menu, 
  X, 
  Car, 
  Rocket,
  LayoutDashboard, 
  Search, 
  PlusCircle, 
  User, 
  LogOut, 
  Moon, 
  Sun,
  ChevronRight,
  ShieldCheck,
  Building2,
  ShoppingBag,
  Video,
  Droplets,
  Briefcase,
  Hammer,
  Scale,
  WashingMachine,
  Sparkles,
  WifiOff,
  Smartphone,
  Radio
} from 'lucide-react';
import { useTheme } from './ThemeProvider';
import { useAuth } from '../contexts/AuthContext';
import { useCountry } from '../contexts/CountryContext';
import { useTenant } from '../contexts/TenantContext';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { Globe, Sparkles as SparklesIcon } from 'lucide-react';
import { Logo } from './ui/Logo';
import { resolveLogoUrl } from '../lib/utils';
import { triggerPWAInstall } from './PWAInstallPrompt';
import { SmartAssistant } from './SmartAssistant';
import * as Sentry from '@sentry/react';

import { motion, AnimatePresence } from 'motion/react';

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Layout Component
 * 
 * Main application layout with role-aware navbar and sidebar.
 * Uses AuthContext to display user profile and filter navigation.
 */
export const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  useEffect(() => {
    setIsSidebarOpen(window.innerWidth > 768);
    const handleResize = () => {
      setIsSidebarOpen(window.innerWidth > 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const { isDarkMode, toggleDarkMode } = useTheme();
  const { tenant, countryTenant, isStorefront, urlPrefix } = useTenant();
  const { profile, user, signOut } = useAuth();
  const { selectedCountry, setSelectedCountry, availableCountries } = useCountry();
  const location = useLocation();
  const navigate = useNavigate();

  // Dashboard routes are an authenticated workspace, not the public discovery experience.
  // Keep public marketplace/category navigation out of Business/Admin/Employee dashboards.
  // Storefronts remain public tenant experiences and continue using their own navigation.
  const isDashboardArea = location.pathname === '/business' || location.pathname.startsWith('/business/') ||
                          location.pathname === '/employee' || location.pathname.startsWith('/employee/') ||
                          location.pathname === '/admin' || location.pathname.startsWith('/admin/');

  const publicNavItems = [
    { name: isStorefront ? 'Storefront' : 'Home', path: urlPrefix || '/', icon: Home, roles: ['platform_admin', 'admin', 'business', 'customer', 'guest', 'tenant_manager', 'employee'] },
    { name: 'Marketplace', path: (urlPrefix || '') + '/marketplace', icon: ShoppingBag, roles: ['platform_admin', 'admin', 'business', 'customer', 'guest', 'tenant_manager', 'employee'] },
    { name: 'Sourcing Hub', path: '/sourcing', icon: Radio, roles: ['platform_admin', 'admin', 'business', 'customer', 'guest', 'tenant_manager', 'employee'] },
    { name: 'Automotive', path: '/automotive', icon: Car, roles: ['platform_admin', 'admin', 'business', 'customer', 'guest', 'tenant_manager', 'employee'] },
    { name: 'Laundry', path: '/laundry', icon: WashingMachine, roles: ['platform_admin', 'admin', 'business', 'customer', 'guest', 'tenant_manager', 'employee'] },
    { name: 'House Cleaning', path: '/cleaning', icon: Sparkles, roles: ['platform_admin', 'admin', 'business', 'customer', 'guest', 'tenant_manager', 'employee'] },
    { name: 'Water Services', path: '/water', icon: Droplets, roles: ['platform_admin', 'admin', 'business', 'customer', 'guest', 'tenant_manager', 'employee'] },
    { name: 'Construction', path: '/construction', icon: Hammer, roles: ['platform_admin', 'admin', 'business', 'customer', 'guest', 'tenant_manager', 'employee'] },
    { name: 'Solar Energy', path: '/solar', icon: Sun, roles: ['platform_admin', 'admin', 'business', 'customer', 'guest', 'tenant_manager', 'employee'] },
    { name: 'Professional', path: '/professional-services', icon: Scale, roles: ['platform_admin', 'admin', 'business', 'customer', 'guest', 'tenant_manager', 'employee'] },
    { name: 'Staff Space', path: '/employee', icon: LayoutDashboard, roles: ['employee'] },
    { name: 'Business Hub', path: '/business', icon: Building2, roles: ['business'] },
    { name: 'Admin Panel', path: '/admin', icon: ShieldCheck, roles: ['platform_admin', 'admin', 'tenant_manager'] },
  ];

  const dashboardNavItems = [
    ...(profile?.role === 'business'
      ? [{ name: 'Business Hub', path: '/business', icon: Building2, roles: ['business'] }]
      : []),
    ...(profile?.role === 'employee'
      ? [{ name: 'Staff Space', path: '/employee', icon: LayoutDashboard, roles: ['employee'] }]
      : []),
    ...(['platform_admin', 'admin', 'tenant_manager'].includes(profile?.role || '')
      ? [{ name: 'Admin Panel', path: '/admin', icon: ShieldCheck, roles: ['platform_admin', 'admin', 'tenant_manager'] }]
      : []),
  ];

  const navItems = (isDashboardArea ? dashboardNavItems : publicNavItems).filter(item => {
    if (item.roles.includes('guest') && !profile) return true;
    return profile && item.roles.includes(profile.role);
  });

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const getRoleBadge = () => {
    if (!profile) return null;
    
    switch (profile.role) {
      case 'platform_admin':
        return (
          <div className="hidden lg:flex items-center gap-1 px-2 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded-full border border-amber-200 dark:border-amber-800 text-[10px] font-bold uppercase tracking-wider">
            <ShieldCheck className="w-3 h-3" />
            Platform Owner
          </div>
        );
      case 'admin':
        return (
          <div className="hidden lg:flex items-center gap-1 px-2 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded-full border border-amber-200 dark:border-amber-800 text-[10px] font-bold uppercase tracking-wider">
            <ShieldCheck className="w-3 h-3" />
            Administrator
          </div>
        );
      case 'tenant_manager':
        return (
          <div className="hidden lg:flex items-center gap-1 px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 rounded-full border border-purple-200 dark:border-purple-800 text-[10px] font-bold uppercase tracking-wider">
            <ShieldCheck className="w-3 h-3" />
            Market Manager
          </div>
        );
      case 'business':
        return (
          <div className="hidden lg:flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-full border border-blue-200 dark:border-blue-800 text-[10px] font-bold uppercase tracking-wider">
            <Building2 className="w-3 h-3" />
            {profile.business_type === 'registered' ? 'Registered Business' : 'Micro Business'}
          </div>
        );
      case 'customer':
        return (
          <div className="hidden lg:flex items-center gap-1 px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full border border-green-200 dark:border-green-800 text-[10px] font-bold uppercase tracking-wider">
            <ShoppingBag className="w-3 h-3" />
            Customer
          </div>
        );
      default:
        return null;
    }
  };

  const getNavbarTitle = () => {
    if (isStorefront && tenant) return tenant.business_name;
    if (countryTenant) return countryTenant.name;
    if (!profile) return 'myservicehub africa';
    
    // Determine if we are in a dedicated dashboard area
    const isDashboardArea = location.pathname.startsWith('/business') || 
                           location.pathname.startsWith('/employee') || 
                           location.pathname.startsWith('/admin');

    if (!isDashboardArea) return 'myservicehub africa';

    switch (profile.role) {
      case 'platform_admin':
      case 'admin':
        return 'myservicehub africa - Admin';
      case 'tenant_manager':
        return 'Market Administration';
      case 'business':
        return `Business Dashboard - ${profile.full_name || 'Provider'}`;
      case 'employee':
        return 'Staff Space';
      default:
        return 'myservicehub africa';
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground transition-colors duration-300">
      {/* Navbar */}
      <nav className="fixed top-0 z-50 w-full bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 shadow-sm">
        <div className="px-3 py-3 lg:px-5 lg:pl-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center justify-start rtl:justify-end">
              <button
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                className="inline-flex items-center p-2 text-sm text-slate-500 rounded-lg sm:hidden hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-200 dark:text-slate-400 dark:hover:bg-slate-700 dark:focus:ring-slate-600"
              >
                <Menu className="w-6 h-6" />
              </button>
              <Link to={urlPrefix || "/"} className="flex ms-2 md:me-24 items-center gap-2 group">
                {isStorefront && resolveLogoUrl(tenant?.logo_url || (tenant as any)?.logo) ? (
                  <img src={resolveLogoUrl(tenant?.logo_url || (tenant as any)?.logo)!} className="h-8 me-3 object-contain" alt="Logo" referrerPolicy="no-referrer" />
                ) : countryTenant?.logo_url ? (
                  <img src={resolveLogoUrl(countryTenant.logo_url) || countryTenant.logo_url} className="h-8 me-3 object-contain" alt="Logo" referrerPolicy="no-referrer" />
                ) : (
                  <Logo showText={false} variant={isDarkMode ? 'white' : 'light'} className="h-8" />
                )}
                <span className="self-center text-lg font-black sm:text-xl whitespace-nowrap tracking-tight font-heading uppercase">
                  {getNavbarTitle()}
                </span>
              </Link>
            </div>
            <div className="flex items-center gap-2">
              {/* Country Selector */}
              <div className={cn(
                "hidden sm:flex items-center gap-2 px-3 py-1.5 bg-slate-100 dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700",
                profile?.role === 'tenant_manager' && "opacity-50 pointer-events-none"
              )}>
                <Globe className="w-4 h-4 text-blue-600" />
                <select 
                  disabled={profile?.role === 'tenant_manager'}
                  value={selectedCountry}
                  onChange={(e) => setSelectedCountry(e.target.value as any)}
                  className="bg-transparent text-xs font-bold outline-none cursor-pointer"
                >
                  {availableCountries.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              {getRoleBadge()}
              
              <button
                onClick={triggerPWAInstall}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 dark:bg-blue-950/50 hover:bg-blue-100 dark:hover:bg-blue-900/50 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-800 rounded-xl text-xs font-bold transition-all cursor-pointer"
                title="Install App on Phone"
              >
                <Smartphone className="w-3.5 h-3.5" />
                <span className="hidden md:inline">Install App</span>
              </button>

              <button
                onClick={toggleDarkMode}
                className="p-2 text-slate-500 rounded-lg hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
              >
                {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
              
              {profile ? (
                <>
                  <button
                    onClick={handleSignOut}
                    className="sm:hidden flex items-center justify-center p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                    title="Log out"
                    aria-label="Log out"
                  >
                    <LogOut className="w-5 h-5" />
                  </button>
                  <div className="flex items-center ms-1 sm:ms-3 gap-2 sm:gap-4">
                  <div className="flex items-center gap-3">
                    <div className="hidden md:block text-right">
                      <p className="text-sm font-medium">{profile?.full_name || user?.email}</p>
                      <p className="text-xs text-slate-500 capitalize">{profile?.role || 'User'}</p>
                    </div>
                    <button className="flex text-sm bg-slate-800 rounded-full focus:ring-4 focus:ring-slate-300 dark:focus:ring-slate-600">
                      <img 
                        className="w-8 h-8 rounded-full object-cover" 
                        src={profile?.avatar_url || `https://ui-avatars.com/api/?name=${profile?.full_name || 'User'}&background=random`} 
                        alt="user photo" 
                        referrerPolicy="no-referrer" 
                      />
                    </button>
                  </div>
                  {/* Account actions: compact icon on desktop, explicit text on mobile */}
                  <button
                    onClick={handleSignOut}
                    className="hidden sm:flex items-center gap-1.5 px-2.5 py-2 text-sm font-semibold text-slate-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                    title="Log out"
                    aria-label="Log out"
                  >
                    <LogOut className="w-5 h-5" />
                    <span className="hidden md:inline">Log out</span>
                  </button>
                  </div>
                </>
              ) : (
                <div className="flex items-center gap-2 ms-3">
                  <Link 
                    to="/marketplace" 
                    className="hidden md:block px-4 py-2 text-sm font-bold text-slate-600 hover:text-blue-600 transition-colors"
                  >
                    Marketplace
                  </Link>
                  <Link 
                    to="/login" 
                    className="hidden md:block px-4 py-2 text-sm font-bold text-slate-600 hover:text-blue-600 transition-colors"
                  >
                    Login
                  </Link>
                  <Link 
                    to="/marketplace" 
                    className="px-4 py-2 bg-blue-600 text-white text-sm font-bold rounded-xl hover:bg-blue-700 transition-all shadow-lg shadow-blue-600/20"
                  >
                    Get Started
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed top-0 left-0 z-40 h-screen pt-20 transition-transform bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800",
          isSidebarOpen ? "translate-x-0 w-64" : "-translate-x-full sm:translate-x-0 sm:w-20"
        )}
      >
        <div className="h-full px-3 pb-4 overflow-y-auto bg-white dark:bg-slate-900">
          <ul className="space-y-2 font-medium">
            {navItems.map((item) => (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={cn(
                    "flex items-center p-2 rounded-lg group transition-colors",
                    location.pathname === item.path
                      ? "bg-[var(--primary)] text-white"
                      : "text-slate-900 dark:text-white hover:bg-slate-100 dark:hover:bg-slate-800"
                  )}
                >
                  <item.icon className={cn("w-5 h-5 transition duration-75", location.pathname === item.path ? "text-white" : "text-slate-500 dark:text-slate-400 group-hover:text-slate-900 dark:group-hover:text-white")} />
                  {isSidebarOpen && <span className="ms-3">{item.name}</span>}
                </Link>
              </li>
            ))}
            
            <li className="mt-2">
              <button
                onClick={triggerPWAInstall}
                className={cn(
                  "w-full flex items-center p-2 rounded-lg group transition-colors text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 cursor-pointer",
                  !isSidebarOpen && "justify-center"
                )}
                title="Install ServiceHub Africa Mobile App"
              >
                <Smartphone className={cn("w-5 h-5 shrink-0 transition duration-75", "text-blue-600 dark:text-blue-400")} />
                {isSidebarOpen && <span className="ms-3 text-sm font-bold">Install Mobile App</span>}
              </button>
            </li>
            <li className="mt-1">
              <a
                href="https://www.makeugc.ai/?ref=bizai"
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                  "flex items-center p-2 rounded-lg group transition-colors text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800",
                  !isSidebarOpen && "justify-center"
                )}
                title="Promote with UGC Videos"
              >
                <Video className={cn("w-5 h-5 shrink-0 transition duration-75", "text-slate-500")} />
                {isSidebarOpen && <span className="ms-3 text-sm font-bold">Create UGC Videos</span>}
              </a>
            </li>
          </ul>
        </div>
      </aside>

      {/* Main Content */}
      <div className={cn("p-4 transition-all duration-300", isSidebarOpen ? "sm:ml-64" : "sm:ml-20")}>
        <div className="p-4 mt-14">
          <AnimatePresence mode="wait">
            <motion.main
              key={location.pathname}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
            >
              {children}
            </motion.main>
          </AnimatePresence>
        </div>
        
        {/* Footer */}
        <footer className="mt-20 pt-12 pb-8 border-t border-slate-100 dark:border-slate-800">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex flex-wrap justify-center gap-6">
              <Link to="/terms" className="text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-blue-600 transition-colors">Terms & Conditions</Link>
              <Link to="/privacy" className="text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-blue-600 transition-colors">Privacy Policy</Link>
              <Link to="/acceptable-use" className="text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-blue-600 transition-colors">Acceptable Use</Link>
              <Link to="/disclaimer" className="text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-blue-600 transition-colors">Disclaimer</Link>
            </div>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
              © 2026 myservicehub africa. All rights reserved.
            </p>
          </div>
        </footer>
        {/* Sourcing Hub Assistant Trigger & Engine */}
        <SmartAssistant />
      </div>

      {/* Offline Play Mode Indicator */}
      <AnimatePresence>
        {isOffline && (
          <motion.div 
            initial={{ opacity: 0, y: 50, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 50, scale: 0.95 }}
            className="fixed bottom-6 right-6 z-50 max-w-sm bg-amber-500 text-white p-4 rounded-3xl shadow-2xl shadow-amber-500/20 border border-amber-400/30 flex items-start gap-3"
          >
            <div className="bg-amber-600 p-2 rounded-2xl shrink-0 mt-0.5">
              <WifiOff className="w-5 h-5 text-amber-100" />
            </div>
            <div>
              <p className="text-xs font-black uppercase tracking-widest text-amber-50">Offline Play Mode</p>
              <p className="text-[10px] opacity-90 mt-1 font-medium leading-normal">
                You are offline. You can browse cached listings, view previously loaded stores, and test out local SME tools. Real bookings and enquiries require internet.
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
